"use client";

import { useState } from "react";
import { useTRPC } from "@/trpc/react";
import { useQuery } from "@tanstack/react-query";

const CATEGORIES = [
  { value: "waves_survived" as const, label: "Most Waves Survived" },
  { value: "rounds_completed" as const, label: "Rounds Completed" },
  { value: "most_loot" as const, label: "Most Loot Earned" },
  { value: "pet_level" as const, label: "Highest Pet Level" },
];

export default function LeaderboardPage() {
  const trpc = useTRPC();
  const [category, setCategory] = useState<"waves_survived" | "rounds_completed" | "most_loot" | "pet_level">("waves_survived");

  const { data: entries, isLoading } = useQuery(
    trpc.rounds.getLeaderboard.queryOptions({ category, limit: 50 })
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-3xl font-black text-center text-amber-400 uppercase tracking-wider mb-2">Leaderboard</h1>
      <div className="flex justify-center mb-8">
        <span className="text-sm text-white/25">☘️ Top Defenders ☘️</span>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setCategory(cat.value)}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              category === cat.value
                ? "bg-green-500 text-black"
                : "bg-white/5 border border-white/10 text-white/40 hover:border-green-500/30"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-white/25">Loading leaderboard...</div>
      ) : !entries || entries.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-white/40 font-bold">No entries yet. Be the first to defend!</p>
        </div>
      ) : (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="py-3 px-4 text-left text-xs font-bold text-white/25 uppercase tracking-wider">Rank</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-white/25 uppercase tracking-wider">Defender</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-white/25 uppercase tracking-wider">Level</th>
                <th className="py-3 px-4 text-right text-xs font-bold text-white/25 uppercase tracking-wider">Score</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4">
                    <span className={`text-sm font-black ${
                      index === 0 ? "text-amber-400" :
                      index === 1 ? "text-white/50" :
                      index === 2 ? "text-amber-600" : "text-white/25"
                    }`}>
                      #{index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-bold text-white/50">
                      {entry.player.displayName ?? `${entry.player.walletAddress.slice(0, 6)}...${entry.player.walletAddress.slice(-4)}`}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-white/25">Lv. {entry.player.playerLevel}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-sm font-black text-amber-400">{entry.score.toLocaleString()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
